import { ApplicationGroupingService, ApplicationServerService, DatabaseApplicationService } from '@amzn/awscat-portfolio-assessment-service-client';
import { ApplicationToServer, DatabaseApplication } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Box, ButtonDropdown, ButtonDropdownProps, NonCancelableCustomEvent } from '@amzn/awsui-components-react';
import { useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router';

import { constants } from '../../../common/Localization';
import AWSCATTable from '../../../common/table/Table-index';
import { ColumnDefinition, ColumnTypes } from '../../../common/table/types';
import { MatchParam } from '../../../common/types';
import ConfirmationModal from '../../common/ConfirmationModal';
import { changeAppDatabaseMappingStatus, changeAppServerMappingStatus } from './ApplicationGroupHelper';
import { STATUS_IN_GROUPING } from './constants';

type Props = FlashContextInterface & {
    onConfirm: () => void;
    onDismiss: () => void;
    appServerMappings: ApplicationToServer[];
    appDatabaseMappings: DatabaseApplication[];
};

type Dependency = {
    resourceType: 'Shared Server' | 'Shared Database';
    resourceId: string;
    applicationId: string;
    status: STATUS_IN_GROUPING;
    newStatus: STATUS_IN_GROUPING;
};

const applicationGroupingService = new ApplicationGroupingService();
const appServerService = new ApplicationServerService();
const databaseApplicationService = new DatabaseApplicationService();

function InReviewDependencyModal({ appServerMappings, appDatabaseMappings, onConfirm, onDismiss, flash }: Props): JSX.Element {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });
    const portfolioId = match?.params?.portfolioId || '';

    const [ loading, setLoading ] = useState(false);
    const [ dirtyCount, setDirtyCount ] = useState(0);
    const [ dependencies, setDependencies ] = useState<Dependency[]>([]);
    const [ selectedDependencies, setSelectedDependencies ] = useState<Dependency[]>([]);

    const onConfirmClick = async () => {
        setLoading(true);
        try {
            await applicationGroupingService.createGroups(portfolioId);
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.ERROR_CREATING_GROUPS);
        } finally {
            onConfirm();
            setLoading(false);
        }
        
    };

    useEffect(() => {
        const serverDependenciesInReview: Dependency[] = appServerMappings.filter(
            mapping => mapping.statusInGrouping === STATUS_IN_GROUPING.IN_REVIEW && mapping.server && !mapping.server.isSharedInfrastructure)
            .map(mapping => ({
                resourceId: mapping.serverId,
                applicationId: mapping.applicationId,
                resourceType: 'Shared Server',
                status: mapping.statusInGrouping as STATUS_IN_GROUPING,
                newStatus: mapping.statusInGrouping as STATUS_IN_GROUPING,
            }));
        
        const databaseDependenciesInReview: Dependency[] = appDatabaseMappings.filter(
            mapping => mapping.statusInGrouping === STATUS_IN_GROUPING.IN_REVIEW && mapping.databaseMappings && 
            !mapping.databaseMappings.isSharedInfrastructure)
            .map(mapping => ({
                resourceId: mapping.databaseId,
                applicationId: mapping.applicationId,
                resourceType: 'Shared Database',
                status: mapping.statusInGrouping as STATUS_IN_GROUPING,
                newStatus: mapping.statusInGrouping as STATUS_IN_GROUPING,
            }));
        
        setDependencies(([] as Dependency[]).concat(serverDependenciesInReview, databaseDependenciesInReview));
    }, [ appServerMappings, appDatabaseMappings ]);

    const onStatusChangeClick = async ({ detail }: NonCancelableCustomEvent<ButtonDropdownProps.ItemClickDetails>): Promise<void> => {
        const updatedStatus = detail.id as STATUS_IN_GROUPING;
        const mappingsToUpdate: Dependency[] = [];
        selectedDependencies.forEach(dependency => {
            const { resourceId, applicationId } = dependency;
            const item = dependencies.find(mapping => mapping.applicationId === applicationId && mapping.resourceId === resourceId);
            if (item) {
                item.newStatus = updatedStatus;
                mappingsToUpdate.push(item);
            }
        });
        setSelectedDependencies([]);
        setDirtyCount(countDirtyDependencies());
        setDependencies([...dependencies]);

        if (mappingsToUpdate.length) {
            setLoading(true);
            try {
                changeAppServerMappingStatus(
                    mappingsToUpdate.filter(mapping => mapping.resourceType === 'Shared Server').map(mapping => ({
                        applicationId: mapping.applicationId,
                        serverId: mapping.resourceId,
                        status: mapping.newStatus as string,
                    })),
                    portfolioId,
                    appServerService,
                    true,
                );
                changeAppDatabaseMappingStatus(
                    mappingsToUpdate.filter(mapping => mapping.resourceType === 'Shared Database').map(mapping => ({
                        applicationId: mapping.applicationId,
                        databaseId: mapping.resourceId,
                        status: mapping.newStatus as string,
                    })),
                    portfolioId,
                    databaseApplicationService,
                    true,
                );
            } catch (err) {
                flash.alert(FlashType.error, (err as Error).message, constants.ERROR_UPDATING_IN_REVIEW_DEPENDENCIES);
            } finally {
                setLoading(false);
            }
        }
    };

    const countDirtyDependencies = (): number => {
        return dependencies.reduce((count, dependency) => count + (dependency.newStatus !== dependency.status ? 1 : 0), 0);
    };

    const headerActions = (
        <ButtonDropdown
            items={[
                {
                    id: STATUS_IN_GROUPING.ACTIVE,
                    text: constants.MARK_AS_ACTIVE,
                },
                {
                    id: STATUS_IN_GROUPING.INACTIVE,
                    text: constants.MARK_AS_INACTIVE,
                },
                {
                    id: STATUS_IN_GROUPING.IN_REVIEW,
                    text: constants.MARK_FOR_REVIEW,
                },
            ]}
            onItemClick={onStatusChangeClick}
            disabled={!selectedDependencies.length}
        >
            {constants.UPDATE_BUTTON_TEXT}
        </ButtonDropdown>
    );

    const columnDefinitions: ColumnDefinition<Dependency>[] = [
        {
            header: constants.RESOURCE_TYPE,
            type: ColumnTypes.TEXT,
            id: 'resourceType',
            cell: item => item.resourceType,
            sortingField: 'resourceType',
        },
        {
            header: constants.RESOURCE_ID,
            type: ColumnTypes.TEXT,
            id: 'resourceId',
            cell: item => item.resourceId,
            sortingField: 'resourceId',
        },
        {
            header: constants.APPLICATION_ID,
            type: ColumnTypes.TEXT,
            id: 'applicationId',
            cell: item => item.applicationId,
            sortingField: 'applicationId',
        },
        {
            header: constants.STATUS,
            type: ColumnTypes.TEXT,
            id: 'status',
            data: item => item.newStatus,
            cell: item => item.newStatus,
            sortingField: 'status',
        },
    ];

    const onTableSelectionChange = (selections: readonly Dependency[] | undefined): void => {
        setSelectedDependencies(selections ? [...selections] : []);
    };

    return (
        <ConfirmationModal
            visible
            header={constants.REMOVE_DEPENDENCIES_BY_TYPE}
            confirmButton={constants.CONTINUE_BUTTON_TEXT}
            confirmLoading={loading}
            onConfirm={onConfirmClick}
            onDismiss={onDismiss}
        >
            <Box>
                <AWSCATTable
                    resourceName={constants.IN_REVIEW_DEPENDENCIES}
                    columnDefinitions={columnDefinitions}
                    items={dependencies}
                    resizeable
                    loading={loading}
                    headerActions={headerActions}
                    selectionType='multi'
                    onSelectionChange={onTableSelectionChange}
                    enableAutoSorting
                    autoGenerateVisibleContent
                    useDefaultDownload
                />
                <Box float='right'>
                    <p>
                        {`${dirtyCount} ${dirtyCount === 1 ?
                            constants.RESOURCE_LOWERCASE :
                            constants.RESOURCES_LOWERCASE} ${constants.MODIFIED}.`}
                    </p>
                </Box>
            </Box>
        </ConfirmationModal>
    );
}
export default withFlashContext(InReviewDependencyModal);
